import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Ticket,
  Euro,
  Clock,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  PieChart,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  Target,
  Zap
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Componente de tarjeta KPI
const KPICard = ({ title, value, change, changeType, icon: Icon, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[#6B7280] font-medium">{title}</p>
          <p className="text-3xl font-semibold text-[#111827] mt-2">{value}</p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${
              changeType === 'positive' ? 'text-green-600' : 'text-red-600'
            }`}>
              {changeType === 'positive' ? (
                <ArrowUpRight className="w-4 h-4 mr-1" />
              ) : (
                <ArrowDownRight className="w-4 h-4 mr-1" />
              )}
              <span>{change}</span>
              <span className="text-[#6B7280] ml-1">vs mes anterior</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};

// Componente de lista de actividad
const ActivityItem = ({ icon: Icon, title, description, time, status }) => {
  const statusColors = {
    success: 'text-green-600 bg-green-50',
    warning: 'text-yellow-600 bg-yellow-50',
    error: 'text-red-600 bg-red-50',
    info: 'text-blue-600 bg-blue-50'
  };

  return (
    <div className="flex items-start gap-4 p-4 hover:bg-gray-50 rounded-xl transition-colors">
      <div className={`p-2 rounded-lg ${statusColors[status] || statusColors.info}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#111827]">{title}</p>
        <p className="text-sm text-[#6B7280] truncate">{description}</p>
      </div>
      <span className="text-xs text-[#9CA3AF] whitespace-nowrap">{time}</span>
    </div>
  );
};

export default function GerenciaPanel() {
  const { user } = useAuth();
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    facturacion: { value: '€127.450', change: '+12.5%', type: 'positive' },
    tickets: { value: '234', change: '-8%', type: 'positive' },
    clientes: { value: '89', change: '+5', type: 'positive' },
    tiempoRespuesta: { value: '2.4h', change: '-15%', type: 'positive' },
    satisfaccion: { value: '94%', change: '+2%', type: 'positive' },
    pendientes: { value: '12', change: '+3', type: 'negative' }
  });

  const [recentActivity] = useState([
    { icon: Euro, title: 'Factura pagada', description: 'Cliente: Restaurante El Molino - €1.250', time: 'Hace 5 min', status: 'success' },
    { icon: Ticket, title: 'Ticket escalado', description: 'Ticket #1234 - Urgente: Sistema caído', time: 'Hace 15 min', status: 'warning' },
    { icon: Users, title: 'Nuevo cliente', description: 'Hotel Costa Brava se ha registrado', time: 'Hace 1h', status: 'info' },
    { icon: CheckCircle, title: 'Proyecto completado', description: 'Instalación TPV - Bar Central', time: 'Hace 2h', status: 'success' },
    { icon: AlertTriangle, title: 'SLA en riesgo', description: 'Ticket #1198 - 4h sin respuesta', time: 'Hace 3h', status: 'error' }
  ]);

  const [departmentStats] = useState([
    { name: 'SAT', tickets: 45, resolved: 38, pending: 7 },
    { name: 'Comercial', tickets: 23, resolved: 20, pending: 3 },
    { name: 'Admin', tickets: 12, resolved: 10, pending: 2 },
    { name: 'Proyectos', tickets: 8, resolved: 6, pending: 2 }
  ]);

  useEffect(() => {
    // Simular carga de datos
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">Dashboard Gerencia</h1>
          <p className="text-[#6B7280] mt-1">Resumen ejecutivo del rendimiento</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-[#111827] focus:outline-none focus:border-[#3B82F6]"
          >
            <option value="today">Hoy</option>
            <option value="week">Esta semana</option>
            <option value="month">Este mes</option>
            <option value="quarter">Este trimestre</option>
            <option value="year">Este ano</option>
          </select>
        </div>
      </div>

      {/* KPIs principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <KPICard
          title="Facturacion"
          value={kpis.facturacion.value}
          change={kpis.facturacion.change}
          changeType={kpis.facturacion.type}
          icon={Euro}
          color="green"
        />
        <KPICard
          title="Tickets resueltos"
          value={kpis.tickets.value}
          change={kpis.tickets.change}
          changeType={kpis.tickets.type}
          icon={CheckCircle}
          color="blue"
        />
        <KPICard
          title="Clientes activos"
          value={kpis.clientes.value}
          change={kpis.clientes.change}
          changeType={kpis.clientes.type}
          icon={Users}
          color="purple"
        />
        <KPICard
          title="Tiempo respuesta"
          value={kpis.tiempoRespuesta.value}
          change={kpis.tiempoRespuesta.change}
          changeType={kpis.tiempoRespuesta.type}
          icon={Clock}
          color="yellow"
        />
        <KPICard
          title="Satisfaccion"
          value={kpis.satisfaccion.value}
          change={kpis.satisfaccion.change}
          changeType={kpis.satisfaccion.type}
          icon={Target}
          color="green"
        />
        <KPICard
          title="Tickets pendientes"
          value={kpis.pendientes.value}
          change={kpis.pendientes.change}
          changeType={kpis.pendientes.type}
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* Segunda fila: Actividad y Departamentos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Actividad reciente */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#111827]">Actividad reciente</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {recentActivity.map((activity, index) => (
              <ActivityItem key={index} {...activity} />
            ))}
          </div>
          <div className="px-6 py-3 border-t border-gray-100">
            <button className="text-sm text-[#3B82F6] hover:text-[#2563EB] font-medium">
              Ver toda la actividad
            </button>
          </div>
        </div>

        {/* Rendimiento por departamento */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#111827]">Rendimiento por departamento</h2>
          </div>
          <div className="p-6 space-y-4">
            {departmentStats.map((dept) => (
              <div key={dept.name} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-[#111827]">{dept.name}</span>
                  <span className="text-[#6B7280]">
                    {dept.resolved}/{dept.tickets} resueltos
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#3B82F6] rounded-full transition-all duration-500"
                    style={{ width: `${(dept.resolved / dept.tickets) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tercera fila: Metricas adicionales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Franquicias */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="font-semibold text-[#111827]">Franquicias</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">Activas</span>
              <span className="font-medium text-[#111827]">24</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">En proceso</span>
              <span className="font-medium text-[#111827]">3</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">Facturacion media</span>
              <span className="font-medium text-[#111827]">€5.280</span>
            </div>
          </div>
        </div>

        {/* SLA */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-50 rounded-lg">
              <Zap className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-[#111827]">SLA</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">Cumplimiento</span>
              <span className="font-medium text-green-600">96.5%</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">Tiempo medio</span>
              <span className="font-medium text-[#111827]">1.8h</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">Incumplimientos</span>
              <span className="font-medium text-red-600">4</span>
            </div>
          </div>
        </div>

        {/* Proyectos */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-[#111827]">Proyectos</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">En curso</span>
              <span className="font-medium text-[#111827]">8</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">Completados (mes)</span>
              <span className="font-medium text-[#111827]">12</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#6B7280]">Valor total</span>
              <span className="font-medium text-[#111827]">€45.600</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
